import React, {useEffect, useMemo, useState} from 'react'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {useTranslation} from 'react-i18next'
import {ReactComponent as DropdownIcon} from 'assets/icons/dropdown.svg'
import {Loader} from 'components/Shared/Loader'
import {
  fetchFinishedOrdersCount,
  getOrderAllowedStatusChanges,
  OrderActionType,
  OrderStatus,
  setOrderStatus,
} from 'services/OrderService'
import Confetti from 'components/Order/OrderDetails/OrderStatusSelectField/Confetti'
import {StatusChangeDialog} from '../StatusChangeDialog'
import styles from './OrderStatusSelectField.module.css'
import {HTTP_400_ERROR, HTTP_403_ERROR, HTTP_404_ERROR, HTTP_409_ERROR, HTTP_422_ERROR} from 'Constants'
import {useErrorHandling} from 'context/errorHandling'
import {CARRIER_VERIFICATION_REQUIRED, INVALID_STATUS_CHANGE, MISSING_OR_INACTIVE_POLICY} from 'services/errorCodes'
import {ORDER_APPROVE_FOR_FULFILLMENT, useAuth} from 'context/auth'
import moment from 'moment-timezone'
import {ErrorDialog} from "../../../Shared/Dialogs";

export function OrderStatusSelectField({
                                         order,
                                         onStatusChange,
                                         actions,
                                         disabled,
                                         isMerchantLimitExceeded,
                                         verificationWarnings
                                       }) {
  const [allowedStatuses, setAllowedStatuses] = useState(null)
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false)
  const {t} = useTranslation()
  const [isConfettiShown, setConfettiShown] = useState(false)
  const [newStatus, setNewStatus] = useState()
  const [minForwardStatusChangeDateTime, setMinForwardStatusChangeDateTime] = useState(null)
  const {hasAnyPrivileges} = useAuth()
  const [warningVerificationFailedDialogOpen, setWarningVerificationFailedDialogOpen] = useState(false)

  const currentOrderStatus = useMemo(() => {
    const {status, deliveryPointNumber, intermediatePointNumber, intermediatePointPosition} = order.status
    return ({
      status, deliveryPointNumber, intermediatePointNumber, intermediatePointPosition,
      backwardStatus: false,
      clientNotified: false
    })
  }, [order.status])

  const {handleError} = useErrorHandling()

  const statusChanges = useMemo(() => actions.filter(action => action.actionType === OrderActionType.OrderStatusChange), [actions])
  const isEntitledToApproveOrderForFulfillment = hasAnyPrivileges([ORDER_APPROVE_FOR_FULFILLMENT])

  useEffect(() => {
    getOrderAllowedStatusChanges(order.id)
      .then(response => {
        let statuses = response.statuses.reverse()
        if (!isEntitledToApproveOrderForFulfillment && isMerchantLimitExceeded) {
          const allowedForAdminStatusOption = 'ACCEPTED_FOR_FULFILLMENT'
          statuses = statuses.filter(allowedStatus => allowedStatus.status !== allowedForAdminStatusOption)
        }
        setAllowedStatuses(statuses)
        setMinForwardStatusChangeDateTime(response.minNewStatusDateTime)
      })
  }, [order, actions, isEntitledToApproveOrderForFulfillment, isMerchantLimitExceeded])

  function changeStatus(formValues) {
    if (formValues.confirmed) {
      setOrderStatus(order.id, newStatus, formValues)
        .then(statusChange => {
            onStatusChange({
              status: statusChange.status,
              timeZone: moment.tz(statusChange.timeZone).zoneAbbr(),
              deliveryPointNumber: statusChange.deliveryPointPosition,
              intermediatePointPosition: statusChange.intermediatePointPosition,
              intermediatePointNumber: newStatus.intermediatePointNumber
            })
            if (statusChange.status === OrderStatus.Finished) {
              fetchFinishedOrdersCount()
                .then(count => {
                  if (count > 0 && count % 100 === 0) {
                    setConfettiShown(true)
                  }
                })
            }
          },
        )
        .catch(error => {
          const errorCode = error?.response?.data?.errorCode
          handleError(error, {
            [HTTP_403_ERROR]: 'notifications.error.noOrderStatusChangePrivileges',
            [HTTP_404_ERROR]: 'notifications.error.orderNotFound',
            [HTTP_422_ERROR]: errorCode === 'DRIVER_REQUIRED' ?
              'notifications.error.driverNotFound' :
              errorCode === 'UNSUPPORTED_STATUS' ?
                'notifications.error.unsupportedStatus' :
                errorCode === INVALID_STATUS_CHANGE ?
                  'notifications.error.invalidStatusChange' : 'notifications.error.orderStatusChange',
            [HTTP_400_ERROR]: errorCode && [MISSING_OR_INACTIVE_POLICY].includes(errorCode) ? `notifications.error.${errorCode}` :
            'notifications.error.acceptForFulfillment',
            [HTTP_409_ERROR]: errorCode && [CARRIER_VERIFICATION_REQUIRED].includes(errorCode) ? `notifications.error.${errorCode}` :
            'notifications.error.acceptForFulfillment'
          })
        })
    }
  }

  const getMinNewStatusDateTime = () => {
    const indexOfNewStatusInExistingActions = actions.findIndex(action => action.status === newStatus.status && (action.actionType === OrderActionType.OrderStatusChange || action.deliveryPointNumber === newStatus.deliveryPointNumber))
    return indexOfNewStatusInExistingActions > -1 ? actions[indexOfNewStatusInExistingActions]?.creationDateTime : minForwardStatusChangeDateTime
  }

  function validateStatusChange(newStatus) {
    if (newStatus.status === OrderStatus.AcceptedForFulfillment) {
      if (Object.values(verificationWarnings).some(item => item === true)) {
        setWarningVerificationFailedDialogOpen(true)
      } else {
        setNewStatus(newStatus)
        setChangeStatusDialogOpen(true)
      }
    } else {
      setNewStatus(newStatus)
      setChangeStatusDialogOpen(true)
    }
  }

  return (
    <>
      {
        newStatus &&
        <StatusChangeDialog open={changeStatusDialogOpen}
                            order={order}
                            newStatus={newStatus}
                            statusChanges={statusChanges}
                            minNewStatusDateTime={getMinNewStatusDateTime()}
                            onClose={(result) => {
                              setChangeStatusDialogOpen(false)
                              changeStatus(result)
                            }}/>
      }
      <ErrorDialog open={warningVerificationFailedDialogOpen}
                   onClose={() => {
                     setWarningVerificationFailedDialogOpen(false)
                   }}
                   content={t('order.details.carrierVerificationFailed')}
      />
      {isConfettiShown && <Confetti/>}
      {allowedStatuses && currentOrderStatus ? (
          <Autocomplete
            id="OrderStatusSelectField"
            options={[currentOrderStatus, ...allowedStatuses]}
            disableClearable
            filterSelectedOptions
            disabled={disabled}
            getOptionLabel={option => t(`order.orderStatusChange.status.${option.status}`, {
              deliveryPointNumber: option.deliveryPointNumber,
              intermediatePointNumber: option.intermediatePointNumber
            })
            }
            value={currentOrderStatus}
            onChange={(e, newStatus) => {
              validateStatusChange(newStatus)
            }}
            noOptionsText={t('form.select.noOptionsText')}
            renderInput={params => (
              <TextField
                {...params}
                label={t('order.orderStatusChange.statusChangeSelectLabel')}
                variant="filled"
                fullWidth
                InputProps={{...params.InputProps, disableUnderline: true}}
                className={styles.orderSelect}
              />
            )}
            popupIcon={<DropdownIcon/>}
          />
        )
        : <Loader/>
      }
    </>
  )
}
